/**
 * Contact FAQ Component (FAQ = Frequently Asked Questions / Preguntas Frecuentes)
 * 
 * Displays a list of Frequently Asked Questions (FAQ) related to the embroidery business.
 * This component:
 * - Shows a warning icon and section title
 * - Contains an array of question-answer pairs
 * - Renders each FAQ item with an expandable/collapsible interface
 * 
 * FAQs cover common customer inquiries about:
 * - Minimum order quantities
 * - Delivery times
 * - Custom garment options
 * - File requirements for logos
 * 
 * @component
 * @returns {JSX.Element} FAQ section with expandable question items
 */

import React from 'react';
import FAQItem from '../faq-item/faq-item'; // FAQ = Frequently Asked Questions
import './contact-faq-style.css';

const ContactFAQ = () => {
  /**
   * Array of FAQ (Frequently Asked Questions) objects
   * Each object contains a question and its corresponding answer
   * @type {Array<{question: string, answer: string}>}
   */
  const faqs = [
    {
      question: '¿Cuál es el pedido mínimo?',
      answer: 'No tenemos pedido mínimo. Puedes solicitar desde 1 prenda en adelante.'
    },
    {
      question: '¿Cuánto tardan en entregar?',
      answer: 'El tiempo de entrega es de 15 a 22 días, dependiendo de la cantidad de prendas solicitadas y de la carga de trabajo actual.'
    },
    {
      question: '¿Puedo llevar mis propias prendas?',
      answer: 'Sí, puedes traer tus propias prendas para realizar el bordado.'
    },
    {
      question: '¿El diseño del bordado tiene un costo adicional?',
      answer: 'No, el diseño del bordado no tiene costo adicional.'
    },
    {
      question: '¿Realizan uniformes escolares y empresariales?',
      answer: 'Sí, realizamos uniformes escolares y empresariales.'
    },
    {
      question: '¿Cómo puedo solicitar una cotización?',
      answer: 'Puedes solicitar una cotización a través de correo electrónico o WhatsApp.'
    },
    {
      question: '¿Hacen envíos fuera de Orotina?',
      answer: 'Sí, realizamos envíos mediante Correos de Costa Rica o encomienda.'
    },
    {
      question: '¿Qué formas de pago aceptan?',
      answer: 'Aceptamos SINPE, transferencia bancaria y efectivo.'
    },
    {
      question: '¿Qué formas de pago aceptan?',
      answer: 'Aceptamos SINPE, transferencia bancaria y efectivo.'
    },
    {
      question: '¿Los sábados trabajan normalmente?',
      answer: 'Sí, trabajamos los sábados de 8:00 a.m. a 12:00 m.d. En algunas ocasiones utilizamos ese día para reabastecer materiales, por lo que la atención al cliente puede verse limitada.'
    }

  ];

  /**
   * Warning Icon Component
   * SVG icon representing an attention/information symbol (triangle with exclamation mark)
   * Used to draw attention to the FAQ section
   * @returns {JSX.Element} Warning/attention icon SVG
   */
  const WarningIcon = () => (
    <svg width="32" height="32" fill="none" viewBox="0 0 32 32">
      <path d="M16 2.66667L29.3333 25.3333H2.66667L16 2.66667Z" stroke="#D6C36A" strokeWidth="2.66667" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M16 12V17.3333" stroke="#D6C36A" strokeWidth="2.66667" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M16 22.6667H16.0133" stroke="#D6C36A" strokeWidth="2.66667" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <div className="contact-faq">
      {/* FAQ section header with icon and title */}
      <div className="contact-faq__header">
        <div className="contact-faq__icon">
          <WarningIcon />
        </div>
        <h2 className="contact-faq__title">Preguntas Frecuentes</h2>
      </div>
      {/* List container for all FAQ (Frequently Asked Questions) items */}
      <div className="contact-faq__list">
        {/* Map through FAQ items and render each as an expandable question/answer */}
        {faqs.map((faq, index) => (
          <FAQItem
            key={index}
            question={faq.question}
            answer={faq.answer}
          />
        ))}
      </div>
    </div>
  );
};

export default ContactFAQ;
